import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

/**
 * Die Bereiche der Checkliste, als Daten aus dem Server.
 *
 * Bis zum 03.09.2026 stand die Liste als Konstante im Paket shared. Sie war der
 * Zuschnitt eines einzigen Betriebs, Immobilie, Pool, Whirlpool, Garten, und
 * genau das darf ein Produkt nicht vorgeben. Seither legt jeder Kunde seine
 * eigenen an, und vier Masken brauchen dieselbe Liste: der Baukasten, die
 * Objektcheckliste, die Berichtsuebersicht und die Abteilungen im Team.
 *
 * Deshalb ein Modul und keine vier Ladefunktionen. Der Zwischenspeicher haelt
 * die Antwort fuer die Dauer des Besuchs: es sind wenige Zeilen, sie aendern
 * sich selten, und eine Maske, die beim Wechsel des Reiters erneut fragt,
 * flackert ohne Gegenwert. Wer sie aendert, ruft neuLaden auf, und alle Masken
 * bekommen den neuen Stand.
 */

export interface Bereichsangabe {
  id: string;
  key: string;
  name_i18n: Record<string, string>;
  sort_order: number;
  is_active: boolean;
}

let zwischenspeicher: Bereichsangabe[] | null = null;
let laufend: Promise<Bereichsangabe[]> | null = null;
const horcher = new Set<(b: Bereichsangabe[]) => void>();

async function holen(): Promise<Bereichsangabe[]> {
  // Zwei Masken, die gleichzeitig starten, sollen eine Anfrage ausloesen und
  // nicht zwei. Deshalb wird das laufende Versprechen geteilt.
  if (!laufend) {
    laufend = api.get<{ bereiche: Bereichsangabe[] }>("/bereiche")
      .then((r) => {
        zwischenspeicher = r.bereiche;
        for (const h of horcher) h(r.bereiche);
        return r.bereiche;
      })
      .finally(() => { laufend = null; });
  }
  return laufend;
}

/** Nach einer Aenderung: alle Masken holen sich den neuen Stand. */
export function bereicheNeuLaden(): Promise<Bereichsangabe[]> {
  zwischenspeicher = null;
  return holen();
}

export function useBereiche(): {
  bereiche: Bereichsangabe[] | null;
  neuLaden: () => Promise<Bereichsangabe[]>;
} {
  const [bereiche, setBereiche] = useState<Bereichsangabe[] | null>(zwischenspeicher);

  useEffect(() => {
    let lebt = true;
    const horcht = (b: Bereichsangabe[]) => { if (lebt) setBereiche(b); };
    horcher.add(horcht);
    if (!zwischenspeicher) void holen().catch(() => { if (lebt) setBereiche([]); });
    else setBereiche(zwischenspeicher);
    return () => { lebt = false; horcher.delete(horcht); };
  }, []);

  return { bereiche, neuLaden: useCallback(() => bereicheNeuLaden(), []) };
}

/**
 * Der Name eines Bereichs in der Sprache des Lesers, sonst sein Schluessel.
 *
 * Die Sprachwahl steht hier noch einmal und nicht als Aufruf von beschriftung
 * aus baukasten.tsx: jene Maske holt sich die Bereiche aus diesem Modul, und
 * zwei Dateien, die einander laden, sind eine Falle fuer den naechsten, der
 * hier etwas verschiebt.
 */
export function bereichsName(
  bereiche: Bereichsangabe[] | null,
  schluessel: string,
  sprache: string
): string {
  const n = bereiche?.find((x) => x.key === schluessel)?.name_i18n;
  if (!n) return schluessel;
  return n[sprache] || n.de || n.en || Object.values(n)[0] || schluessel;
}
